import type { ButtonProps } from '../../button/Button';
import type { Dispatch, ReactElement, ReactNode } from 'react';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Fragment } from 'react';
import { keyframes, styled } from 'styled-components';

const showDropDown = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const hideDropDown = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const StyledContent = styled(DropdownMenuPrimitive.Content)`
  background-color: ${(props) => props.theme.colors.white};
  border-radius: 0.5rem;
  box-shadow: 0px 8px 8px ${(props) => props.theme.colors.shadows.oxfordBlue[10]};
  padding: 0.5rem 0;
  /* Uncapped, a long item list grows past the viewport and those items are unreachable.
     border-box so the cap covers the padding too, or the card overhangs the edge. */
  box-sizing: border-box;
  max-height: var(--radix-popper-available-height);
  overflow-y: auto;
  overscroll-behavior: contain;

  &[data-state='open'] {
    animation: ${showDropDown} 0.15s ease-out both;
  }
  &[data-state='closed'] {
    animation: ${hideDropDown} 0.1s ease-in both;
  }
`;

const StyledItem = styled(DropdownMenuPrimitive.Item)`
  display: flex;
  gap: 0.625rem;
  padding: 0.5rem 1rem;
  font-size: ${(props) => props.theme.fontSizes.s};
  line-height: ${(props) => props.theme.lineHeights.s};
  font-family: ${(props) => props.theme.fontFamily.montserrat};
  font-weight: 500;
  color: ${(props) => props.theme.colors.oxfordBlue[80]};

  &:hover,
  &:focus {
    outline: none;
    background-color: ${(props) => props.theme.colors.slateGray[10]};
    cursor: pointer;
  }

  svg {
    height: 1rem;
    width: auto;
  }
`;

const StyledSeparator = styled(DropdownMenuPrimitive.Separator)`
  height: 1px;
  width: 80%;
  margin: 0 auto;
  background-color: ${(props) => props.theme.colors.slateGray[10]};
`;

const StyledDropdownItemContent = styled.span`
  display: flex;
  align-items: center;
`;

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuContent = StyledContent;
const DropdownMenuItem = StyledItem;
const DropdownMenuSeparator = StyledSeparator;

export type Item = {
  abbreviation?: string;
  icon?: ReactElement;
  label: string;
};

export type SelectProps = {
  activeItemIndex: number;
  buttonProps?: Pick<ButtonProps, 'size' | 'type' | 'data-test-id'>;
  items: Item[];
  onItemChange: (selectedItemIndex: number) => void;
};

export type DefaultSelectProps = {
  children: ReactNode;
  isOpen: boolean;
  items: Item[];
  onItemChange: (selectedItemIndex: number) => void;
  setIsOpen: Dispatch<boolean>;
};

export const Select = ({ children, isOpen, items, onItemChange, setIsOpen }: DefaultSelectProps) => {
  return (
    <DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent collisionPadding={8} sideOffset={5}>
        {items.map((item, index) => (
          <Fragment key={item.label}>
            <DropdownMenuItem
              onSelect={() => {
                onItemChange(index);
              }}
            >
              <StyledDropdownItemContent>{item.icon}</StyledDropdownItemContent>
              <StyledDropdownItemContent>{item.label}</StyledDropdownItemContent>
            </DropdownMenuItem>
            {index < items.length - 1 && <DropdownMenuSeparator />}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
